import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PLAN_LABELS = { onetime: 'One-Time Report — $5.99', monthly: 'Monthly Plan — $29.99/mo', yearly: 'Yearly Plan — $299.99/yr' };

export default function Register() {
    const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const plan = new URLSearchParams(location.search).get('plan');

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (form.password !== form.confirm) {
            setError('Passwords do not match');
            return;
        }
        if (form.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            await register(form.email, form.name, form.password);
            if (plan) {
                navigate(`/checkout-options?plan=${plan}`);
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const googleLogin = () => {
        window.location.href = plan ? `/api/auth/google?plan=${plan}` : '/api/auth/google';
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="font-extrabold text-3xl" style={{ color: '#1a1a2e' }}>
                        Seller <span style={{ color: '#e94560' }}>Source</span>
                    </Link>
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Create your account</h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {plan ? 'Sign up to complete your purchase' : 'Start finding profitable arbitrage deals'}
                    </p>
                </div>

                {plan && PLAN_LABELS[plan] && (
                    <div className="mb-4 px-4 py-3 rounded-xl flex items-center gap-3 text-sm font-semibold"
                        style={{ background: 'rgba(233,69,96,0.08)', border: '1.5px solid rgba(233,69,96,0.3)', color: '#c73652' }}>
                        <i className="fas fa-shopping-cart flex-shrink-0"></i>
                        <span>Selected: {PLAN_LABELS[plan]}</span>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                            <i className="fas fa-exclamation-circle flex-shrink-0"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    <button onClick={googleLogin}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all text-sm mb-6">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign Up with Google
                    </button>

                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs text-gray-400 bg-white px-3">or register with email</div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
                            <input type="text" value={form.name} onChange={set('name')} required placeholder="John Doe"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm transition-colors" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                            <input type="email" value={form.email} onChange={set('email')} required placeholder="you@example.com"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm transition-colors" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                            <input type="password" value={form.password} onChange={set('password')} required placeholder="Min 6 characters"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm transition-colors" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm Password</label>
                            <input type="password" value={form.confirm} onChange={set('confirm')} required placeholder="Repeat password"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm transition-colors" />
                        </div>
                        <button type="submit" disabled={loading}
                            className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{ background: '#e94560' }}>
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Creating Account...
                                </span>
                            ) : 'Create Free Account'}
                        </button>
                    </form>
                </div>

                <p className="text-center text-sm text-gray-500 mt-6">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold" style={{ color: '#e94560' }}>Sign in</Link>
                </p>
            </div>
        </div>
    );
}
